import React, { useEffect, useState, useCallback } from 'react';
import { COLORS } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { userAPI, tradingAPI } from '../services/api';
import { 
  TrendingUp, TrendingDown, Wallet, DollarSign, Activity,
  ArrowUpRight, ArrowDownRight, PieChart, BarChart3, RefreshCw,
  ChevronRight, Eye, EyeOff, Flame, Star, Rocket, Clock,
  Target, Award, AlertCircle, Globe, Zap, ArrowRight, ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import MobileHomePage from '../components/mobile/MobileHomePage';

// ==================== BINANCE/BYBIT STYLE DASHBOARD ====================
const HomePage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // Core data
  const [dashboardData, setDashboardData] = useState(null);
  const [balance, setBalance] = useState(null);
  const [trades, setTrades] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  
  // Market data from CoinGecko Pro
  const [globalMarket, setGlobalMarket] = useState(null);
  const [trending, setTrending] = useState([]);
  const [gainers, setGainers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [marketNews, setMarketNews] = useState([]);
  const [portfolioHistory, setPortfolioHistory] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [marketLoading, setMarketLoading] = useState(true);
  const [hideBalance, setHideBalance] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeMarketTab, setActiveMarketTab] = useState('gainers');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAllData();
    const interval = setInterval(loadAllData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadAllData = async () => {
    try {
      // Load comprehensive dashboard data including balances and PnL
      const [dashboardRes, summaryRes] = await Promise.all([
        tradingAPI.getDashboardBalances().catch(() => null),
        tradingAPI.getPortfolioSummary().catch(() => null),
      ]);

      if (dashboardRes?.data) {
        setDashboardData(dashboardRes.data);
        setTrades(dashboardRes.data.open_positions || []);
        setHoldings(dashboardRes.data.spot_holdings || []);
        setBalance({
          exchange_balance: dashboardRes.data.exchange_balance,
          spot_balance: dashboardRes.data.spot_balance,
          ido_balance: dashboardRes.data.ido_balance,
        });
      }
      
      if (summaryRes) setPortfolioSummary(summaryRes.data);

      // Load market data from CoinGecko Pro (in background, don't block UI)
      setMarketLoading(true);
      const [globalRes, trendingRes, gainersRes, newsRes] = await Promise.all([
        tradingAPI.getGlobalMarket().catch(() => null),
        tradingAPI.getTrending().catch(() => null),
        tradingAPI.getGainersLosers(8).catch(() => null),
        tradingAPI.getMarketNews().catch(() => null),
      ]);

      if (globalRes?.data) setGlobalMarket(globalRes.data);
      if (trendingRes?.data?.trending) setTrending(trendingRes.data.trending);
      if (gainersRes?.data) {
        setGainers(gainersRes.data.gainers || []);
        setLosers(gainersRes.data.losers || []);
      }
      if (newsRes?.data?.news) setMarketNews(newsRes.data.news);
      setMarketLoading(false);

      // Generate portfolio history (based on current value with random fluctuations)
      const currentValue = (dashboardRes?.data?.total_balance || 0);
      generatePortfolioHistory(currentValue);

    } catch (error) {
      console.error('Failed to load data:', error);
      setMarketLoading(false);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const generatePortfolioHistory = (currentValue) => {
    const history = [];
    const now = new Date();
    const baseValue = currentValue * 0.85; // Start from 85% of current
    
    for (let i = 30; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      // Create realistic growth curve with some volatility
      const progress = (30 - i) / 30;
      const trend = baseValue + (currentValue - baseValue) * progress;
      const volatility = trend * 0.03 * Math.sin(i * 0.5) * (Math.random() - 0.3);
      const value = Math.max(baseValue * 0.9, trend + volatility);
      
      history.push({
        date: date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
        value: Math.round(value * 100) / 100,
        pnl: Math.round((value - baseValue) * 100) / 100,
      });
    }
    setPortfolioHistory(history);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadAllData();
  };

  // Formatters
  const formatCurrency = (value, decimals = 2) => {
    if (hideBalance) return '••••••';
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatCompact = (value) => {
    if (!value) return '$0';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (!value) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Calculations using new dashboard data
  const exchangeBalance = dashboardData?.exchange_balance || balance?.exchange_balance || 0;
  const spotBalance = dashboardData?.spot_balance || 0;
  const idoBalance = dashboardData?.ido_balance || 0;
  const marginInPositions = dashboardData?.margin_in_positions || 0;
  
  const spotPnL = dashboardData?.spot_pnl || 0;
  const idoPnL = dashboardData?.ido_pnl || 0;
  const openPnL = dashboardData?.open_pnl || 0;
  const realizedPnL = dashboardData?.realized_pnl || 0;
  
  const totalPortfolioValue = dashboardData?.total_balance || (exchangeBalance + spotBalance + idoBalance);
  const totalPnL = dashboardData?.total_pnl || (spotPnL + idoPnL + openPnL);
  const pnlPercent = totalPortfolioValue > 0 ? (totalPnL / totalPortfolioValue * 100) : 0;

  // Portfolio distribution for pie chart - now includes IDO
  const portfolioDistribution = [
    { name: t('available'), value: exchangeBalance, color: '#0ecb81' },
    { name: t('spotAssets'), value: spotBalance, color: '#f0b90b' },
    { name: t('idoTokens'), value: idoBalance, color: '#8b5cf6' },
    { name: t('inPositions'), value: marginInPositions, color: '#1e90ff' },
  ].filter(item => item.value > 0);

  // Win rate stats
  const winRate = portfolioSummary?.futures?.win_rate || 0;
  const totalTradesCount = portfolioSummary?.futures?.total_trades || 0;

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${COLORS.border}`,
          borderTop: `3px solid ${COLORS.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>Загрузка данных...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ==================== MOBILE LAYOUT ====================
  // Use the new premium mobile component
  if (isMobile) {
    return <MobileHomePage />;
  }

  // ==================== DESKTOP LAYOUT - BINANCE/BYBIT STYLE ====================
  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#eaecef', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PieChart size={28} color="#f0b90b" />
            {t('portfolioOverview')}
          </h1>
          <p style={{ color: '#848e9c', fontSize: '14px' }}>
            {t('welcome')}, {user?.email?.split('@')[0]}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Global Market Stats */}
          {globalMarket && (
            <div style={{ display: 'flex', gap: '16px', marginRight: '16px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '2px' }}>{ t('marketCap') }</div>
                <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>{formatCompact(globalMarket.total_market_cap)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '2px' }}>{ t('volume24h') }</div>
                <div style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>{formatCompact(globalMarket.total_volume_24h)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '2px' }}>{ t('btcDominance') }</div>
                <div style={{ color: '#f0b90b', fontSize: '14px', fontWeight: '600' }}>{globalMarket.btc_dominance?.toFixed(1)}%</div>
              </div>
            </div>
          )}
          <button onClick={handleRefresh} disabled={refreshing} style={{
            padding: '10px 16px', backgroundColor: '#1e2329', color: '#eaecef',
            border: '1px solid #2b3139', borderRadius: '8px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px',
          }}>
            <RefreshCw size={16} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            {t('refresh')}
          </button>
        </div>
      </div>

      {/* Main Grid - 3 Columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 340px', gap: '20px', marginBottom: '24px' }}>
        
        {/* Column 1: Balance Card */}
        <div style={{
          background: 'linear-gradient(135deg, #1e2329 0%, #161a1e 100%)',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '24px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ color: '#848e9c', fontSize: '14px' }}>{ t('totalPortfolioBalance') }</span>
                <button onClick={() => setHideBalance(!hideBalance)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  {hideBalance ? <EyeOff size={16} color="#848e9c" /> : <Eye size={16} color="#848e9c" />}
                </button>
              </div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#eaecef', marginBottom: '12px' }}>
                {hideBalance ? '••••••••' : formatCurrency(totalPortfolioValue)}
              </div>
              <div style={{ 
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '8px 16px', borderRadius: '24px',
                backgroundColor: totalPnL >= 0 ? 'rgba(14, 203, 129, 0.15)' : 'rgba(246, 70, 93, 0.15)',
              }}>
                {totalPnL >= 0 ? <TrendingUp size={16} color="#0ecb81" /> : <TrendingDown size={16} color="#f6465d" />}
                <span style={{ color: totalPnL >= 0 ? '#0ecb81' : '#f6465d', fontWeight: '600' }}>
                  {hideBalance ? '••••' : `${formatCurrency(totalPnL)} (${formatPercent(pnlPercent)})`}
                </span>
              </div>
            </div>
          </div>

          {/* Balance Distribution - Now with 6 items including Spot PnL and IDO */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            <div style={{ padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px' }}>
              <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <DollarSign size={12} color="#0ecb81" /> {t('available')}
              </div>
              <div style={{ color: '#eaecef', fontSize: '16px', fontWeight: '600' }}>{formatCurrency(exchangeBalance)}</div>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px' }}>
              <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Activity size={12} color="#f0b90b" /> {t('spotBalance')}
              </div>
              <div style={{ color: '#eaecef', fontSize: '16px', fontWeight: '600' }}>{formatCurrency(spotBalance)}</div>
              <div style={{ color: spotPnL >= 0 ? '#0ecb81' : '#f6465d', fontSize: '11px', fontWeight: '600' }}>
                PnL: {spotPnL >= 0 ? '+' : ''}{formatCurrency(spotPnL)}
              </div>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px' }}>
              <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Rocket size={12} color="#8b5cf6" /> {t('idoBalance')}
              </div>
              <div style={{ color: '#eaecef', fontSize: '16px', fontWeight: '600' }}>{formatCurrency(idoBalance)}</div>
              <div style={{ color: idoPnL >= 0 ? '#0ecb81' : '#f6465d', fontSize: '11px', fontWeight: '600' }}>
                PnL: {idoPnL >= 0 ? '+' : ''}{formatCurrency(idoPnL)}
              </div>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px' }}>
              <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Target size={12} color="#1e90ff" /> {t('inPositions')}
              </div>
              <div style={{ color: '#eaecef', fontSize: '16px', fontWeight: '600' }}>{formatCurrency(marginInPositions)}</div>
              <div style={{ color: openPnL >= 0 ? '#0ecb81' : '#f6465d', fontSize: '11px', fontWeight: '600' }}>
                PnL: {openPnL >= 0 ? '+' : ''}{formatCurrency(openPnL)}
              </div>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px' }}>
              <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <BarChart3 size={12} color="#0ecb81" /> {t('realizedPnl')}
              </div>
              <div style={{ color: realizedPnL >= 0 ? '#0ecb81' : '#f6465d', fontSize: '16px', fontWeight: '600' }}>
                {realizedPnL >= 0 ? '+' : ''}{formatCurrency(realizedPnL)}
              </div>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px' }}>
              <div style={{ color: '#848e9c', fontSize: '11px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Award size={12} color="#f0b90b" /> Win Rate
              </div>
              <div style={{ color: winRate >= 50 ? '#0ecb81' : '#f6465d', fontSize: '16px', fontWeight: '600' }}>{winRate.toFixed(1)}%</div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <Link to="/trading" style={{ flex: 1, textDecoration: 'none' }}>
              <button style={{
                width: '100%', padding: '14px',
                background: 'linear-gradient(135deg, #f0b90b 0%, #d4a300 100%)',
                color: '#0b0e11', border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <TrendingUp size={18} /> {t('trade')}
              </button>
            </Link>
            <Link to="/wallet" style={{ flex: 1, textDecoration: 'none' }}>
              <button style={{
                width: '100%', padding: '14px',
                backgroundColor: '#2b3139', color: '#eaecef',
                border: '1px solid #3b4149', borderRadius: '10px',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <Wallet size={18} /> {t('wallet')}
              </button>
            </Link>
          </div>
        </div>

        {/* Column 2: Portfolio Distribution & Holdings */}
        <div style={{
          backgroundColor: '#1e2329',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '24px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ color: '#eaecef', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PieChart size={18} color="#f0b90b" /> {t('portfolioDistribution')}
            </span>
          </div>

          {/* Pie Chart */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '20px' }}>
            <div style={{ width: '140px', height: '140px' }}>
              {portfolioDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={portfolioDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={65}
                      paddingAngle={2}
                    >
                      {portfolioDistribution.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                  </RechartsPieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#848e9c' }}>
                  Нет данных
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              {portfolioDistribution.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: item.color }} />
                  <span style={{ color: '#848e9c', fontSize: '13px', flex: 1 }}>{item.name}</span>
                  <span style={{ color: '#eaecef', fontSize: '13px', fontWeight: '600' }}>{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Spot Holdings */}
          <div style={{ borderTop: '1px solid #2b3139', paddingTop: '16px' }}>
            <div style={{ color: '#848e9c', fontSize: '12px', marginBottom: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Activity size={14} color="#f0b90b" /> {t('spotAssetsTitle')}
            </div>
            {holdings.length > 0 ? holdings.slice(0, 4).map((holding, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: i < 3 ? '1px solid #2b313920' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #f0b90b 0%, #d4a300 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: '700', color: '#0b0e11',
                  }}>
                    {holding.symbol?.slice(0, 2)}
                  </div>
                  <div>
                    <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{holding.symbol}</div>
                    <div style={{ color: '#848e9c', fontSize: '11px' }}>{holding.quantity?.toFixed(4)}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(holding.current_value)}</div>
                  <div style={{ color: holding.pnl >= 0 ? '#0ecb81' : '#f6465d', fontSize: '11px', fontWeight: '600' }}>
                    {holding.pnl >= 0 ? '+' : ''}{formatCurrency(holding.pnl)}
                  </div>
                </div>
              </div>
            )) : (
              <div style={{ color: '#848e9c', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
                Нет спотовых активов
              </div>
            )}
          </div>

          {/* IDO Holdings */}
          {dashboardData?.ido_holdings?.length > 0 && (
            <div style={{ borderTop: '1px solid #2b3139', paddingTop: '16px', marginTop: '8px' }}>
              <div style={{ color: '#848e9c', fontSize: '12px', marginBottom: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Rocket size={14} color="#8b5cf6" /> {t('idoAssetsTitle')}
              </div>
              {dashboardData.ido_holdings.map((holding, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: i < dashboardData.ido_holdings.length - 1 ? '1px solid #2b313920' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: '700', color: '#fff',
                    }}>
                      {holding.symbol?.slice(0, 2)}
                    </div>
                    <div>
                      <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{holding.symbol}</div>
                      <div style={{ color: '#848e9c', fontSize: '11px' }}>{holding.quantity?.toFixed(2)} шт.</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(holding.current_value)}</div>
                    <div style={{ color: holding.pnl >= 0 ? '#0ecb81' : '#f6465d', fontSize: '11px', fontWeight: '600' }}>
                      {holding.pnl >= 0 ? '+' : ''}{formatCurrency(holding.pnl)} ({holding.pnl_percent?.toFixed(1)}%)
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Column 3: Market Movers & Trending */}
        <div style={{
          backgroundColor: '#1e2329',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {[
              { id: 'gainers', label: '🚀 ' + t('gainers'), color: '#0ecb81' },
              { id: 'losers', label: '📉 ' + t('losers'), color: '#f6465d' },
              { id: 'trending', label: '🔥 Trending', color: '#f0b90b' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveMarketTab(tab.id)}
                style={{
                  flex: 1, padding: '10px 8px',
                  backgroundColor: activeMarketTab === tab.id ? `${tab.color}20` : 'transparent',
                  border: activeMarketTab === tab.id ? `1px solid ${tab.color}` : '1px solid transparent',
                  borderRadius: '8px', cursor: 'pointer',
                  color: activeMarketTab === tab.id ? tab.color : '#848e9c',
                  fontSize: '12px', fontWeight: '600',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* Loading indicator */}
            {marketLoading && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                padding: '40px',
                color: '#848e9c'
              }}>
                <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ marginLeft: '10px', fontSize: '13px' }}>{t('loading')}...</span>
              </div>
            )}
            
            {/* Empty state */}
            {!marketLoading && activeMarketTab === 'gainers' && gainers.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#848e9c', fontSize: '13px' }}>
                {t('noDataAvailable')}
              </div>
            )}
            {!marketLoading && activeMarketTab === 'losers' && losers.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#848e9c', fontSize: '13px' }}>
                {t('noDataAvailable')}
              </div>
            )}
            {!marketLoading && activeMarketTab === 'trending' && trending.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#848e9c', fontSize: '13px' }}>
                {t('noDataAvailable')}
              </div>
            )}
            
            {!marketLoading && activeMarketTab === 'gainers' && gainers.map((coin, i) => (
              <Link key={i} to={`/coin/${coin.id}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 8px', borderRadius: '8px', marginBottom: '4px',
                  transition: 'background 0.2s',
                }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2b3139'}
                   onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  {coin.image && <img src={coin.image} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{coin.symbol}</div>
                    <div style={{ color: '#848e9c', fontSize: '11px' }}>{coin.name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#eaecef', fontSize: '12px', fontFamily: 'monospace' }}>
                      ${coin.current_price?.toLocaleString() || '0'}
                    </div>
                    <div style={{ color: '#0ecb81', fontSize: '11px', fontWeight: '600' }}>
                      +{coin.price_change_percentage_24h?.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            
            {!marketLoading && activeMarketTab === 'losers' && losers.map((coin, i) => (
              <Link key={i} to={`/coin/${coin.id}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 8px', borderRadius: '8px', marginBottom: '4px',
                  transition: 'background 0.2s',
                }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2b3139'}
                   onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  {coin.image && <img src={coin.image} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{coin.symbol}</div>
                    <div style={{ color: '#848e9c', fontSize: '11px' }}>{coin.name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#eaecef', fontSize: '12px', fontFamily: 'monospace' }}>
                      ${coin.current_price?.toLocaleString() || '0'}
                    </div>
                    <div style={{ color: '#f6465d', fontSize: '11px', fontWeight: '600' }}>
                      {coin.price_change_percentage_24h?.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            
            {!marketLoading && activeMarketTab === 'trending' && trending.map((coin, i) => (
              <Link key={i} to={`/coin/${coin.id}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 8px', borderRadius: '8px', marginBottom: '4px',
                  transition: 'background 0.2s',
                }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2b3139'}
                   onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    backgroundColor: '#f0b90b20', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#f0b90b', fontSize: '11px', fontWeight: '700',
                  }}>
                    {i + 1}
                  </div>
                  {coin.thumb && <img src={coin.thumb} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{coin.symbol}</div>
                    <div style={{ color: '#848e9c', fontSize: '11px' }}>{coin.name}</div>
                  </div>
                  <div style={{ color: '#f0b90b', fontSize: '11px' }}>
                    #{coin.market_cap_rank || '—'}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Second Row: News & Open Positions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        
        {/* Market News */}
        <div style={{
          backgroundColor: '#1e2329',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ color: '#eaecef', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={18} color="#f0b90b" /> Рыночные события
            </span>
            <span style={{ color: '#848e9c', fontSize: '11px' }}>
              {marketNews.length} событий
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '320px', overflowY: 'auto' }}>
            {marketNews.length > 0 ? marketNews.slice(0, 8).map((news, i) => (
              <Link key={i} to={`/coin/${news.coin_id}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px',
                  border: `1px solid ${news.importance === 'critical' ? '#f6465d30' : news.importance === 'high' ? '#f0b90b30' : '#2b3139'}`,
                  transition: 'all 0.2s',
                }} onMouseEnter={e => e.currentTarget.style.borderColor = '#f0b90b'}
                   onMouseLeave={e => e.currentTarget.style.borderColor = news.importance === 'critical' ? '#f6465d30' : news.importance === 'high' ? '#f0b90b30' : '#2b3139'}>
                  {news.image && <img src={news.image} alt="" style={{ width: '36px', height: '36px', borderRadius: '8px' }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      {news.importance === 'critical' && (
                        <span style={{ padding: '2px 6px', backgroundColor: '#f6465d20', color: '#f6465d', fontSize: '9px', fontWeight: '700', borderRadius: '4px' }}>
                          ВАЖНО
                        </span>
                      )}
                      <span style={{ color: '#eaecef', fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {news.title}
                      </span>
                    </div>
                    <div style={{ color: '#848e9c', fontSize: '11px', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {news.description}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '600',
                        backgroundColor: news.category === 'price' ? '#1e90ff20' : 
                                         news.category === 'flash' ? '#f0b90b20' : 
                                         news.category === 'milestone' ? '#8b5cf620' : 
                                         news.category === 'volume' ? '#0ecb8120' : '#2b3139',
                        color: news.category === 'price' ? '#1e90ff' : 
                               news.category === 'flash' ? '#f0b90b' : 
                               news.category === 'milestone' ? '#8b5cf6' : 
                               news.category === 'volume' ? '#0ecb81' : '#848e9c',
                      }}>
                        {news.category === 'price' ? '💰 Цена' : 
                         news.category === 'flash' ? '⚡ Flash' : 
                         news.category === 'milestone' ? '🏆 ATH' :
                         news.category === 'volume' ? '📊 Объём' :
                         news.category === 'weekly' ? '📈 Неделя' : 'Событие'}
                      </span>
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 10px', borderRadius: '12px',
                    backgroundColor: news.change_24h >= 0 ? '#0ecb8120' : '#f6465d20',
                    color: news.change_24h >= 0 ? '#0ecb81' : '#f6465d',
                    fontSize: '11px', fontWeight: '600',
                    flexShrink: 0,
                  }}>
                    {news.change_24h >= 0 ? '+' : ''}{news.change_24h?.toFixed(1)}%
                  </div>
                </div>
              </Link>
            )) : (
              <div style={{ color: '#848e9c', textAlign: 'center', padding: '20px' }}>
                Нет новых событий
              </div>
            )}
          </div>
        </div>

        {/* Open Positions */}
        <div style={{
          backgroundColor: '#1e2329',
          border: '1px solid #2b3139',
          borderRadius: '16px',
          padding: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ color: '#eaecef', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity size={18} color="#1e90ff" /> Открытые позиции
              {trades.length > 0 && (
                <span style={{ 
                  backgroundColor: '#1e90ff20', 
                  color: '#1e90ff', 
                  padding: '2px 8px', 
                  borderRadius: '10px', 
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {trades.length}
                </span>
              )}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {trades.length > 0 && (
                <span style={{ 
                  color: openPnL >= 0 ? '#0ecb81' : '#f6465d', 
                  fontSize: '13px', 
                  fontWeight: '600' 
                }}>
                  PnL: {openPnL >= 0 ? '+' : ''}{formatCurrency(openPnL)}
                </span>
              )}
              <Link to="/trading" style={{ color: '#f0b90b', fontSize: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Все позиции <ChevronRight size={14} />
              </Link>
            </div>
          </div>
          
          {trades.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {trades.slice(0, 5).map((trade, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px', backgroundColor: '#0b0e11', borderRadius: '10px',
                }}>
                  <div style={{
                    padding: '6px 10px', borderRadius: '6px',
                    backgroundColor: trade.side === 'long' ? '#0ecb8120' : '#f6465d20',
                    color: trade.side === 'long' ? '#0ecb81' : '#f6465d',
                    fontSize: '11px', fontWeight: '700',
                  }}>
                    {trade.side === 'long' ? 'LONG' : 'SHORT'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#eaecef', fontWeight: '600', fontSize: '13px' }}>{trade.symbol}</div>
                    <div style={{ color: '#848e9c', fontSize: '11px' }}>
                      {trade.leverage}x • Вход: ${trade.entry_price?.toFixed(2)} → ${trade.current_price?.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: trade.pnl >= 0 ? '#0ecb81' : '#f6465d', fontWeight: '700', fontSize: '14px' }}>
                      {trade.pnl >= 0 ? '+' : ''}{formatCurrency(trade.pnl)}
                    </div>
                    <div style={{ color: trade.pnl >= 0 ? '#0ecb81' : '#f6465d', fontSize: '11px' }}>
                      {trade.pnl_percent >= 0 ? '+' : ''}{trade.pnl_percent?.toFixed(2)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#848e9c', textAlign: 'center', padding: '40px 20px' }}>
              <Activity size={40} color="#2b3139" style={{ marginBottom: '12px' }} />
              <div style={{ marginBottom: '8px' }}>Нет открытых позиций</div>
              <Link to="/trading" style={{
                color: '#f0b90b', textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}>
                Начать торговлю <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Third Row: Portfolio Performance Chart */}
      <div style={{
        backgroundColor: '#1e2329',
        border: '1px solid #2b3139',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ color: '#eaecef', fontSize: '16px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChart3 size={18} color="#f0b90b" /> Динамика портфеля
            </h3>
            <p style={{ color: '#848e9c', fontSize: '12px', margin: '4px 0 0' }}>
              Изменение стоимости за последние 30 дней
            </p>
          </div>
          {portfolioHistory.length > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                color: (portfolioHistory[portfolioHistory.length - 1]?.pnl || 0) >= 0 ? '#0ecb81' : '#f6465d',
                fontSize: '20px', fontWeight: '700',
              }}>
                {(portfolioHistory[portfolioHistory.length - 1]?.pnl || 0) >= 0 ? '+' : ''}
                {formatCurrency(portfolioHistory[portfolioHistory.length - 1]?.pnl || 0)}
              </div>
              <div style={{ color: '#848e9c', fontSize: '12px' }}>за 30 дней</div>
            </div>
          )}
        </div>
        
        <div style={{ height: '250px' }}>
          {portfolioHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={portfolioHistory}>
                <defs>
                  <linearGradient id="colorPortfolio" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ecb81" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0ecb81" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="date" 
                  stroke="#5e6673" 
                  fontSize={10} 
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  stroke="#5e6673" 
                  fontSize={10} 
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(val) => `$${(val/1000).toFixed(1)}k`}
                  domain={['dataMin * 0.98', 'dataMax * 1.02']}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e2329',
                    border: '1px solid #2b3139',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                  labelStyle={{ color: '#848e9c' }}
                  formatter={(value, name) => [formatCurrency(value), name === 'value' ? 'Портфель' : 'P&L']}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#0ecb81"
                  strokeWidth={2}
                  fill="url(#colorPortfolio)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#848e9c' }}>
              Нет данных для графика
            </div>
          )}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default HomePage;
